/**
 * Quick-add (pane / node context menus) and related actions: open menus at cursor,
 * copy node id, delete node, place entity or file at flow position.
 */

import { buildEntityNodeAtFlowPosition } from "@/pages/canvas/utils/canvasEntityNodes";
import type { Entity } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  attachNodeToSectionIfApplicable,
  buildDetachUpdatesForSectionRemovals,
} from "../utils/sectionMembership";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";
import { useCanvasPermissions } from "./useCanvasPermissions";

export type {
  NodeContextMenuState,
  QuickAddCursorState,
} from "../context/CanvasUiContext";

export interface UseCanvasQuickAddHandlersParams {
  isPlacingTextNote: boolean;
  isPlacingDoc: boolean;
  isPlacingDeferredQuickCompose?: boolean;
  copyNodeIds: (ids: string[]) => void;
  duplicateNodeIds: (ids: string[]) => void;
  /** Right-click quick add — not the explorer/sidebar entry point. */
  onQuickAddOpenedFromPane?: () => void;
}

export { buildQuickAddCursorFromClientPos } from "../utils/quickAddCursor";

export function useCanvasQuickAddHandlers({
  isPlacingTextNote,
  isPlacingDoc,
  isPlacingDeferredQuickCompose = false,
  copyNodeIds,
  duplicateNodeIds,
  onQuickAddOpenedFromPane,
}: UseCanvasQuickAddHandlersParams) {
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow<Node>();
  const {
    reactFlowWrapperRef,
    setNodeContextMenu,
    setQuickAddCursor,
    setShowQuickAdd,
  } = useCanvasUi();
  const { getEntityNodeWidth, getEntityNodeHeight } = useCanvasNodePlacement();
  const { removeNodes, addNode, batchUpdateNodeParents } = useCanvas();
  const handlePaneContextMenu = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      if (!canEdit) return;
      if (isPlacingTextNote || isPlacingDoc || isPlacingDeferredQuickCompose)
        return;

      const wrapperRect = reactFlowWrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;

      setNodeContextMenu(null);
      onQuickAddOpenedFromPane?.();

      setQuickAddCursor({
        screenX: e.clientX - wrapperRect.left,
        screenY: e.clientY - wrapperRect.top,
        clientX: e.clientX,
        clientY: e.clientY,
        flowPosition: reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        }),
      });
      setShowQuickAdd(true);
    },
    [
      canEdit,
      isPlacingTextNote,
      isPlacingDoc,
      isPlacingDeferredQuickCompose,
      reactFlowWrapperRef,
      reactFlowInstance,
      onQuickAddOpenedFromPane,
      setNodeContextMenu,
      setQuickAddCursor,
      setShowQuickAdd,
    ],
  );

  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      if (!canEdit) return;
      e.preventDefault();
      if (isPlacingTextNote || isPlacingDoc || isPlacingDeferredQuickCompose)
        return;

      const wrapperRect = reactFlowWrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;

      setShowQuickAdd(false);
      setQuickAddCursor(null);

      setNodeContextMenu({
        nodeId: node.id,
        nodeType: node.type,
        screenX: e.clientX - wrapperRect.left,
        screenY: e.clientY - wrapperRect.top,
        clientX: e.clientX,
        clientY: e.clientY,
        flowPosition: reactFlowInstance.screenToFlowPosition({
          x: e.clientX,
          y: e.clientY,
        }),
      });
    },
    [
      canEdit,
      isPlacingTextNote,
      isPlacingDoc,
      isPlacingDeferredQuickCompose,
      reactFlowWrapperRef,
      reactFlowInstance,
      setShowQuickAdd,
      setQuickAddCursor,
      setNodeContextMenu,
    ],
  );

  const handleNodeContextMenuCopy = useCallback(
    (nodeId: string) => {
      copyNodeIds([nodeId]);
    },
    [copyNodeIds],
  );

  const handleNodeContextMenuDuplicate = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      duplicateNodeIds([nodeId]);
    },
    [canEdit, duplicateNodeIds],
  );

  // Section-only: remove the section wrapper but detach (keep) its children.
  const handleNodeContextMenuDeleteWrapperOnly = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      const allNodes = reactFlowInstance.getNodes();
      const detach = buildDetachUpdatesForSectionRemovals(
        [nodeId],
        allNodes,
        reactFlowInstance.getInternalNode,
      );
      if (detach.length > 0) {
        batchUpdateNodeParents(detach);
      }
      removeNodes([nodeId]);
    },
    [canEdit, reactFlowInstance, batchUpdateNodeParents, removeNodes],
  );

  const handleNodeContextMenuDelete = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      const allNodes = reactFlowInstance.getNodes();
      const target = allNodes.find((n) => n.id === nodeId);
      if (!target) return;

      const detach = buildDetachUpdatesForSectionRemovals(
        [nodeId],
        allNodes,
        reactFlowInstance.getInternalNode,
      );
      if (detach.length > 0) {
        batchUpdateNodeParents(detach);
      }
      removeNodes([nodeId]);
    },
    [canEdit, reactFlowInstance, batchUpdateNodeParents, removeNodes],
  );

  const handleQuickAddSelectEntity = useCallback(
    (entity: Entity, flowPosition: { x: number; y: number }) => {
      const newNode = buildEntityNodeAtFlowPosition(entity, flowPosition, {
        getEntityNodeWidth,
        getEntityNodeHeight,
      });
      const attached = attachNodeToSectionIfApplicable({
        node: newNode,
        nodes: reactFlowInstance.getNodes(),
        getInternalNode: (id) => reactFlowInstance.getInternalNode(id),
      });
      addNode(attached);
    },
    [addNode, reactFlowInstance, getEntityNodeWidth, getEntityNodeHeight],
  );

  return {
    handlePaneContextMenu,
    handleNodeContextMenu,
    handleNodeContextMenuCopy,
    handleNodeContextMenuDuplicate,
    handleNodeContextMenuDeleteWrapperOnly,
    handleNodeContextMenuDelete,
    handleQuickAddSelectEntity,
  };
}
